import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";
import axios from "axios";
import { ICompareInChartMinimal, IUser } from "..";
import { IFilter, Page } from "../api.types";
import { IOrganizationClauseChecklist } from "../clause-checklists";
import { IClauseList } from "../clauses";
import { IScope } from "../scopes";
import {
    IOrganization,
    IOrganizationContextResponse,
    IOrganizationCreate,
    IOrganizationLogoUpload,
    IOrganizationMemberCreate,
    IOrganizationSettings,
    IOrganizationSettingsUpdate,
    IOrganizationStats,
    IOrganizationUpdate,
    ISignedOrganizationLogo,
} from "./organizations.types";
const API = `${API_URL}/organizations`;

export const createOrganization = async (
    createOrganization: IOrganizationCreate
) => {
    const result = await axios.post<IOrganization>(API, createOrganization);
    return result.data;
};

export const getAllOrganizations = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter,
    search?: string | null
) => {
    const url = buildUrl(API, { page, size, search, ...filter });

    const result = await axios.get<Page<IOrganization>>(url);
    return result.data;
};

export const getOrganizationById = async (id: number) => {
    const result = await axios.get<IOrganization>(`${API}/${id}`);
    return result.data;
};

export const updateOrganizationById = async (
    id: number,
    updateOrganization: IOrganizationUpdate
) => {
    const result = await axios.put<IOrganization>(
        `${API}/${id}`,
        updateOrganization
    );
    return result.data;
};

export const deleteOrganizationById = async (id: number) => {
    const result = await axios.delete(`${API}/${id}`);
    return result.data;
};

export const addOrganizationMembers = async (
    id: number,
    organizationMemberCreate: IOrganizationMemberCreate
) => {
    const result = await axios.post(
        `${API}/${id}/members`,
        organizationMemberCreate
    );
    return result.data;
};

export const removeOrganizationMembers = async (
    id: number,
    user_ids: number[]
) => {
    const params = user_ids.map((id) => `user_ids=${id}`).join("&");

    const result = await axios.delete(`${API}/${id}/members?${params}`);
    return result.data;
};

export const getOrganizationMembers = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/members`, { page, size, ...filter });

    const result = await axios.get<Page<IUser>>(url);
    return result.data;
};

export const getOrganizationScopes = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/scopes`, {
        page,
        size,
        ...filter,
    });

    const result = await axios.get<Page<IScope>>(url);
    return result.data;
};

export const getOrganizationStats = async (id: number) => {
    const result = await axios.get<IOrganizationStats>(`${API}/${id}/stats`);
    return result.data;
};

export const getOrganizationClauseChecklists = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/clause-checklists`, {
        page,
        size,
        ...filter,
    });

    const result = await axios.get<Page<IOrganizationClauseChecklist>>(url);
    return result.data;
};

export const getOrganizationClauses = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/clauses`, {
        page,
        size,
        ...filter,
    });

    const result = await axios.get<Page<IClauseList>>(url);
    return result.data;
};

export const getOrganizationCompareInCharts = async (
    id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const url = buildUrl(`${API}/${id}/compare-in-charts`, {
        page,
        size,
        ...filter,
    });

    const result = await axios.get<Page<ICompareInChartMinimal>>(url);
    return result.data;
};

export const uploadOrganizationLogo = async (
    id: number,
    organizationLogoUpload: IOrganizationLogoUpload
) => {
    const result = await axios.post(
        `${API}/${id}/logo`,
        organizationLogoUpload
    );
    return result.data;
};

export const getOrganizationLogo = async (id: number) => {
    const result = await axios.get<ISignedOrganizationLogo>(
        `${API}/${id}/logo`
    );
    return result.data;
};

export const deleteOrganizationLogo = async (id: number) => {
    const result = await axios.delete(`${API}/${id}/logo`);
    return result.data;
};

export const getOrganizationSettings = async (id: number) => {
    const result = await axios.get<IOrganizationSettings>(
        `${API}/${id}/settings`
    );
    return result.data;
};

export const updateOrganizationSettings = async (
    id: number,
    updateObject: IOrganizationSettingsUpdate
) => {
    const result = await axios.put<IOrganizationSettings>(
        `${API}/${id}/settings`,
        updateObject
    );
    return result.data;
};

export const getOrganizationContext = async () => {
    const result = await axios.get<IOrganizationContextResponse>(
        `${API}/context`
    );
    return result.data;
};
